/**
 * Shared TanStack Query client for Archon.
 *
 * Philosophy (R30 in ARCHITECTURE.md):
 * - We use TanStack Query as a stale-while-revalidate cache: returning to a
 *   page should paint instantly from the in-memory cache while a background
 *   refetch happens. Users only see a loading spinner on the very first
 *   visit to a route, not on every remount.
 * - Defaults are deliberately conservative so modules that have not opted
 *   in behave the same as before. Queries that want SWR semantics override
 *   `staleTime` explicitly.
 * - `refetchOnWindowFocus` is DISABLED globally (R37, Phase 1). With a map on a
 *   TV plus a laptop, every window switch refetched ALL stale queries at once
 *   and exhausted the tenant DB pool. Login is unaffected — auth uses axios
 *   directly, not TanStack Query. Freshness: staleTime, remount, reconnect,
 *   mutation invalidation, and explicit `refetchInterval` on live modules.
 * - `refetchOnReconnect` leverages the browser online event to refresh
 *   after the laptop wakes from sleep or after an offline field trip.
 * - `retry: 1` avoids the 3-retry default that inflates perceived latency
 *   on real 4xx errors; auth errors still surface immediately.
 */
import { QueryClient } from '@tanstack/react-query';

const archonQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Fresh for 60s — fewer background refetches when several panels mount.
      staleTime: 60_000,
      // Keep data in cache for 10 minutes after the last observer unmounts
      // so navigating away and back repaints instantly.
      gcTime: 10 * 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      // Most UIs already show cached data if available, so a soft retry
      // delay is fine. 600ms first retry.
      retryDelay: (attempt) => Math.min(600 * 2 ** attempt, 4_000),
    },
    mutations: {
      retry: 0,
    },
  },
});

export default archonQueryClient;
